export const bunnyShapes = [
  ["classic", "Classic"],
  ["round", "Round"],
  ["tall", "Tall"],
  ["lop", "Lop"],
  ["square", "Square"],
  ["tuft", "Tuft"],
];
export const bunnyAccessories = [
  ["none", "None"],
  ["star", "Star"],
  ["magnifier", "Magnifier"],
  ["glasses", "Glasses"],
  ["headphones", "Headphones"],
  ["hardhat", "Hard hat"],
  ["shield", "Shield"],
  ["clipboard", "Clipboard"],
];
const colors = [
  ["#B8A4FF", "Lilac"],
  ["#60A5FA", "Blue"],
  ["#34D399", "Mint"],
  ["#FBBF24", "Amber"],
  ["#FB7185", "Rose"],
  ["#F472B6", "Pink"],
  ["#FB923C", "Orange"],
  ["#94A3B8", "Slate"],
];
/**
 * ADR-072: round avatar backgrounds, in contract order (BUNNY_BACKGROUNDS in
 * packages/contracts/routes/agents.ts). Deep tones, so every preset body
 * colour keeps at least 3:1 against each (tests/foundation/bunny-backgrounds).
 * @type {[string, string, string | null][]}
 */
export const bunnyBackgrounds = [
  ["none", "No background", null],
  ["indigo", "Indigo", "#3730A3"],
  ["ocean", "Ocean", "#1D4E89"],
  ["teal", "Teal", "#134E4A"],
  ["forest", "Forest", "#1F5132"],
  ["moss", "Moss", "#365314"],
  ["bronze", "Bronze", "#713F12"],
  ["rust", "Rust", "#7C2D12"],
  ["berry", "Berry", "#7A2E55"],
  ["plum", "Plum", "#6B21A8"],
  ["steel", "Steel", "#334155"],
];
const backgroundFill = new Map(
  bunnyBackgrounds.map(([id, , fill]) => [id, fill]),
);
/** The background a bunny has until someone chooses one (same as the server's). */
export function defaultBunnyBackground(id = "") {
  let sum = 0;
  for (const character of [...String(id)].slice(-4))
    sum += character.codePointAt(0) || 0;
  return bunnyBackgrounds[1 + (sum % (bunnyBackgrounds.length - 1))][0];
}
const heads = {
  agentspace:
    "M24 20C14 20 9 28 9 37c0 7 6 10 15 10s15-3 15-10c0-9-5-17-15-17Z",
  round: "M24 22C14 22 7 27 7 35s6 12 17 12 17-4 17-12-7-13-17-13Z",
  tall: "M24 19c-8 0-12 8-12 17s4 11 12 11 12-2 12-11-4-17-12-17Z",
  lop: "M24 23c-9 0-15 5-15 13s5 11 15 11 15-3 15-11-6-13-15-13Z",
  square: "M17 23h14c6 0 9 3 9 9v7c0 6-3 8-9 8H17c-6 0-9-2-9-8v-7c0-6 3-9 9-9Z",
  tuft: "M19 24c-1-4 1-7 4-8l1 5 4-3 1 6c8 2 12 7 12 14s-6 9-17 9-17-2-17-9 4-12 12-14Z",
};
const ears = {
  agentspace:
    "M14 27C13 20 9 6 13 3c5-3 8 13 9 22ZM26 25C26 16 28 1 33 2c5 1 1 18-1 25Z",
  round: "M13 27C11 19 10 7 15 6s7 13 7 21ZM26 27C26 18 28 5 33 6s3 15 1 22Z",
  tall: "M14 26C12 17 10 1 15 1s7 15 7 25ZM26 26C26 14 28 0 33 1s2 17 0 26Z",
  lop: "M14 29C11 20 10 4 15 3s7 16 7 25ZM26 26c-1-11 4-17 10-12 6 5 9 15 6 20-4 5-7-7-10-11l-1 6Z",
  square: "M12 27V8c0-7 9-7 9 0v19ZM27 27V8c0-7 9-7 9 0v19Z",
  tuft: "M14 28C12 20 6 8 11 4s10 13 11 23ZM26 27C27 18 32 1 37 4s-1 17-4 24Z",
};
const accessories = {
  star: "M37 19l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1Z",
  magnifier: "M43 38a5 5 0 1 1-10 0 5 5 0 0 1 10 0ZM42 42l4 4",
  glasses: "M14 30h8v4a4 4 0 0 1-8 0ZM27 30h8v4a4 4 0 0 1-8 0ZM22 32h5",
  headphones:
    "M10 30v-3a14 14 0 0 1 28 0v3h-3v-3a11 11 0 0 0-22 0v3ZM8 29q0-2 2-2h3v11h-3q-2 0-2-2ZM35 27h3q2 0 2 2v7q0 2-2 2h-3Z",
  hardhat: "M12 23a12 12 0 0 1 24 0h4v3H8v-3ZM22 12h4v11h-4Z",
  shield: "M33 34q6 2 12 0v5q0 6-6 9-6-3-6-9Z",
  clipboard:
    "M36 33h6q2 0 2 2v10q0 2-2 2h-6q-2 0-2-2V35q0-2 2-2ZM37 31h4v5h-4Z",
};
const ns = "http://www.w3.org/2000/svg";
export function bunnyActivity(status, runStatus, pendingApproval = false) {
  if (status !== "active") return "paused";
  if (pendingApproval) return "waiting";
  return ["admitted", "running"].includes(runStatus) ? "working" : "idle";
}
/** Use reported activity only; completed tool receipts do not mean a tool is still running. */
export function bunnyMotion(
  state,
  snapshot,
  responding = false,
  failed = false,
  presence,
) {
  if (state === "paused") return "paused";
  if (state === "waiting") return "waiting";
  if (failed || snapshot?.run?.status === "error") return "failed";
  if (presence === "offline") return "offline";
  if (state !== "working") return "resting";
  if (responding) return "responding";
  return snapshot?.run?.status === "admitted" ? "thinking" : "working";
}
function svgNode(tag, attributes) {
  const node = document.createElementNS(ns, tag);
  for (const [key, value] of Object.entries(attributes))
    node.setAttribute(key, String(value));
  return node;
}

export function renderBunny(appearance = {}, state = "idle", size = 32) {
  const shape = Object.hasOwn(heads, appearance.shape)
    ? appearance.shape
    : "classic";
  const color = /^#[a-f\d]{6}$/i.test(appearance.color)
    ? appearance.color
    : "#B8A4FF";
  const icon = svgNode("svg", {
    viewBox: "0 0 48 48",
    width: size,
    height: size,
    "aria-hidden": "true",
    focusable: "false",
    fill: "none",
    stroke: "none",
    class: "bunny-avatar",
    "data-shape": shape,
    "data-state": ["working", "waiting", "paused"].includes(state)
      ? state
      : "idle",
    "data-animated": appearance.animated !== false,
    "data-motion": state === "working" ? "working" : "resting",
  });
  // SEC-02: CSSOM writes are allowed without 'unsafe-inline'; style attributes are not.
  icon.style?.setProperty?.("--bunny-size", `${size}px`);
  const body = svgNode("g", { class: "bunny-body" });
  body.append(
    svgNode("path", { d: ears[shape], fill: color, class: "bunny-ears" }),
    svgNode("path", { d: heads[shape], fill: color }),
    svgNode("path", {
      d: "M15 9Q18 15 18 22M31 9Q30 15 30 22",
      stroke: "#fff7ed",
      "stroke-width": 2,
      "stroke-linecap": "round",
      fill: "#fff7ed",
      opacity: ".35",
    }),
    svgNode("path", {
      d: "M24 36c-5-5-12-2-11 3s6 6 11 6 10-1 11-6-6-8-11-3Z",
      fill: "#fff7ed",
    }),
    svgNode("path", {
      d: "M18.5 29a1.5 1.5 0 0 1 1.5 1.5v2a1.5 1.5 0 0 1-3 0v-2a1.5 1.5 0 0 1 1.5-1.5ZM30.5 29a1.5 1.5 0 0 1 1.5 1.5v2a1.5 1.5 0 0 1-3 0v-2a1.5 1.5 0 0 1 1.5-1.5Z",
      fill: "#24212e",
      class: "bunny-eyes",
    }),
    svgNode("path", {
      d: "M22 35q2-1 4 0 1 1-2 3-3-2-2-3ZM19 39q5 4 10 0-1 5-5 5t-5-5Z",
      class: "bunny-mouth",
      fill: "#24212e",
    }),
    svgNode("path", { d: "M25 40h2v2h-2z", fill: "white" }),
  );
  if (Object.hasOwn(accessories, appearance.accessory))
    body.append(
      svgNode("path", {
        d: accessories[appearance.accessory],
        fill: appearance.accessory === "glasses" ? "none" : "#FBBF24",
        stroke: "#24212e",
        "stroke-width": 1.5,
      }),
    );
  // ADR-072: on a filled circle the bunny shrinks to fit inside it, so ears
  // and accessories stay on the circle; nothing is clipped either way.
  const fill = backgroundFill.get(appearance.background) || null;
  icon.setAttribute("data-background", fill ? appearance.background : "none");
  if (fill) {
    const frame = svgNode("g", {
      class: "bunny-frame",
      transform: "translate(24 24) scale(.76) translate(-24 -24)",
    });
    frame.append(body);
    icon.append(
      svgNode("circle", {
        cx: 24,
        cy: 24,
        r: 24,
        fill,
        class: "bunny-background",
      }),
      frame,
    );
  } else icon.append(body);
  const attention = svgNode("g", { class: "bunny-attention" });
  attention.append(
    svgNode("circle", { cx: 41, cy: 41, r: 5, fill: "#FBBF24" }),
    svgNode("path", { d: "M40 38h2v4h-2zM40 43h2v1h-2z", fill: "#24212e" }),
  );
  icon.append(attention);
  return icon;
}

export function createBunnyAppearance(form) {
  const field = (name) => form.elements.namedItem(name);
  const preview = document.createElement("div");
  preview.className = "bunny-appearance-preview";
  const caption = document.createElement("p");
  caption.textContent = "A little personality for this bunny.";
  const shapes = document.createElement("fieldset");
  shapes.className = "bunny-shapes";
  const legend = document.createElement("legend");
  legend.textContent = "Bunny shape";
  shapes.append(legend);
  for (const [value, name] of bunnyShapes) {
    const label = document.createElement("label");
    const radio = document.createElement("input");
    radio.type = "radio";
    radio.name = "shape";
    radio.value = value;
    radio.checked = value === "classic";
    const text = document.createElement("span");
    text.textContent = name;
    label.append(
      radio,
      renderBunny({ shape: value, animated: false }, "idle", 40),
      text,
    );
    shapes.append(label);
  }
  // BUN-04: eight preset swatches plus a compact custom colour.
  const swatches = document.createElement("fieldset");
  swatches.className = "bunny-color-swatches";
  const colorLegend = document.createElement("legend");
  colorLegend.textContent = "Colour";
  swatches.append(colorLegend);
  const custom = document.createElement("input");
  custom.type = "color";
  custom.name = "color";
  custom.value = colors[0][0];
  custom.className = "bunny-custom-color";
  custom.setAttribute("aria-label", "Custom colour");
  for (const [color, name] of colors) {
    const button = document.createElement("button");
    button.type = "button";
    button.title = name;
    button.setAttribute("aria-label", name);
    button.style.setProperty("--bunny-swatch", color);
    button.dataset.color = color;
    button.addEventListener("click", () => {
      custom.value = color;
      custom.dispatchEvent(new Event("input", { bubbles: true }));
    });
    swatches.append(button);
  }
  const customLabel = document.createElement("label");
  customLabel.className = "bunny-custom-swatch";
  customLabel.title = "Custom colour";
  customLabel.append(custom);
  swatches.append(customLabel);
  // Accessories as cards that show the bunny wearing them.
  const accessoryCards = document.createElement("fieldset");
  accessoryCards.className = "bunny-shapes bunny-accessories";
  const accessoryLegend = document.createElement("legend");
  accessoryLegend.textContent = "Accessory";
  accessoryCards.append(accessoryLegend);
  for (const [value, name] of bunnyAccessories) {
    const label = document.createElement("label");
    const radio = document.createElement("input");
    radio.type = "radio";
    radio.name = "accessory";
    radio.value = value;
    radio.checked = value === "none";
    const text = document.createElement("span");
    text.textContent = name;
    label.append(
      radio,
      renderBunny({ accessory: value, animated: false }, "idle", 40),
      text,
    );
    accessoryCards.append(label);
  }
  // ADR-072: the round background, as swatches showing this bunny on each.
  const backgrounds = document.createElement("fieldset");
  backgrounds.className = "bunny-background-swatches";
  const backgroundLegend = document.createElement("legend");
  const backgroundName = document.createElement("span");
  backgroundName.className = "bunny-background-current";
  backgroundLegend.append("Background ", backgroundName);
  backgrounds.append(backgroundLegend);
  for (const [value, name] of bunnyBackgrounds) {
    const label = document.createElement("label");
    label.className = "bunny-background-swatch";
    label.title = name;
    const radio = document.createElement("input");
    radio.type = "radio";
    radio.name = "background";
    radio.value = value;
    radio.checked = value === "none";
    const text = document.createElement("span");
    text.className = "sr-only";
    text.textContent = name;
    const chip = document.createElement("span");
    chip.className = "bunny-background-chip";
    chip.dataset.background = value;
    label.append(radio, chip, text);
    backgrounds.append(label);
  }
  const motionLabel = document.createElement("label");
  motionLabel.className = "ui-switch-field bunny-motion-toggle";
  const motion = document.createElement("input");
  motion.type = "checkbox";
  motion.name = "animated";
  motion.checked = true;
  motion.className = "ui-switch";
  motion.setAttribute("role", "switch");
  const motionText = document.createElement("span");
  motionText.className = "ui-switch-field__text";
  const motionTitle = document.createElement("span");
  motionTitle.className = "ui-switch-field__label";
  motionTitle.textContent = "Animate";
  const motionHint = document.createElement("span");
  motionHint.className = "ui-switch-field__hint";
  motionHint.textContent =
    "Movement follows activity: thinking, responding, working, or waiting. Reduced-motion settings always win.";
  motionText.append(motionTitle, motionHint);
  motionLabel.append(motion, motionText);
  form.prepend(preview, shapes, swatches, backgrounds, accessoryCards);
  (
    form.querySelector(".actions") ||
    form.querySelector('button[type="submit"]')
  ).before(motionLabel);
  const value = () => ({
    color: field("color").value,
    accessory: field("accessory").value,
    shape: field("shape").value,
    animated: motion.checked,
    background: field("background").value || "none",
  });
  function update() {
    const look = value();
    preview.replaceChildren(renderBunny(look, "idle", 72), caption);
    for (const chip of backgrounds.querySelectorAll(".bunny-background-chip"))
      chip.replaceChildren(
        renderBunny(
          { ...look, animated: false, background: chip.dataset.background },
          "idle",
          36,
        ),
      );
    backgroundName.textContent = `· ${
      (look.background !== "none" &&
        bunnyBackgrounds.find(([id]) => id === look.background)?.[1]) ||
      "None"
    }`;
    for (const icon of form.querySelectorAll(".bunny-shapes .bunny-avatar"))
      icon
        .querySelectorAll(".bunny-ears, .bunny-body > path:nth-child(2)")
        .forEach((path) => {
          path.setAttribute("fill", field("color").value);
        });
    for (const button of swatches.querySelectorAll("button[data-color]"))
      button.setAttribute(
        "aria-pressed",
        String(
          button.dataset.color.toLowerCase() ===
            field("color").value.toLowerCase(),
        ),
      );
  }
  form.addEventListener("input", update);
  form.addEventListener("change", update);
  return {
    value,
    load(appearance) {
      field("color").value = appearance.color;
      field("accessory").value = appearance.accessory;
      field("shape").value = Object.hasOwn(heads, appearance.shape)
        ? appearance.shape
        : "classic";
      motion.checked = appearance.animated !== false;
      field("background").value = backgroundFill.has(appearance.background)
        ? appearance.background
        : "none";
      update();
    },
  };
}

const externalPresence = {
  online: "Online",
  working: "Working",
  offline: "Offline",
};

/** Reflect each rail bunny's activity in its avatar state, tooltip and accessible name. */
export function applyBunnyActivity(container, view) {
  if (!container) return;
  const pending = view.approvals.some(
    (approval) => approval.status === "pending",
  );
  for (const icon of container.querySelectorAll(".bunny-avatar")) {
    const agent = view.agents.find(
      (value) => value.id === icon.dataset.agentId,
    );
    const current =
      !view.projectHome &&
      agent?.id === view.selected?.id &&
      icon.dataset.projectId === (view.projectContext || "");
    const attention =
      view.attentionSnapshot.byBot.find((item) => item.agentId === agent?.id)
        ?.count || 0;
    // ADR-072: the rail shows the count as a badge; the tooltip repeats it.
    const button = icon.parentElement;
    const count = button.querySelector?.(".rail-count");
    if (count) {
      count.textContent = attention > 99 ? "99+" : String(attention);
      count.hidden = !attention;
    }
    const local = bunnyActivity(
      agent?.status,
      current ? view.snapshot?.run?.status : undefined,
      attention > 0 || (current && pending),
    );
    // BUN-01: the server reports every bunny's state; the open chat adds instant feedback.
    const live = view.activity?.get?.(icon.dataset.agentId);
    const state =
      local === "idle" && live && live.state !== "idle"
        ? live.state === "failed"
          ? "idle"
          : live.state
        : local;
    const status = icon.parentElement?.querySelector?.(".bunny-status");
    // EXT-02: an external bunny says whether its agent is connected.
    const presence =
      live?.presence && state !== "waiting"
        ? externalPresence[
            /** @type {keyof typeof externalPresence} */ (live.presence)
          ]
        : undefined;
    if (status)
      status.textContent = presence
        ? presence
        : state === "working"
          ? "Working"
          : state === "waiting"
            ? live?.label || "Needs you"
            : state === "paused"
              ? "Paused"
              : live?.state === "failed"
                ? live.label
                : status.dataset.idle || "";
    icon.dataset.state = state;
    icon.dataset.motion = bunnyMotion(
      state,
      current ? view.snapshot : undefined,
      current && Boolean(view.streaming?.text),
      live?.state === "failed",
      live?.presence,
    );

    // The status badge: activity, or an external agent's connection.
    button.dataset.status =
      state === "waiting" || !live?.presence ? state : live.presence;
    button.setAttribute(
      "aria-label",
      `${icon.dataset.botName}${presence ? `, external agent ${presence.toLowerCase()}` : state === "waiting" ? (current && pending ? ", waiting for approval" : ", needs attention") : state === "working" ? ", working" : state === "paused" ? ", paused" : ""}${attention ? `, ${attention} in Needs you` : ""}`,
    );
  }
}
